import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { loginRequest, msalConfig, apiConfig } from './authConfig'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import axios from 'axios'
import { getRuntimeConfig } from './utils/configUtils'

axios.defaults.baseURL = getRuntimeConfig('REACT_APP_BACKEND_ENDPOINT') || getRuntimeConfig('REACT_APP_API_URL') || ''

const msalInstance = new PublicClientApplication(msalConfig)

await msalInstance.initialize().then(() => {
  console.log('MSAL initialized')
})

export const user = { email: '', admin: false, operations: false, sales: false }

axios.interceptors.request.use(async config => {
  try {
    const account = msalInstance.getActiveAccount()
    if (!account) {
      throw new Error('No active account')
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: apiConfig.scopes,
      account: account,
      authority: apiConfig.authority,
    })
    config.headers.Authorization = `Bearer ${response.accessToken}`
    return config
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: apiConfig.scopes,
          authority: apiConfig.authority,
        })
        config.headers.Authorization = `Bearer ${response.accessToken}`
        return config
      } catch (popupError) {
        console.error('Popup error:', popupError)
        throw popupError
      }
    }
    throw error
  }
})

function AppContainer() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()

  useEffect(() => {
    const handleAuth = async () => {
      if (inProgress !== 'none') {
        return
      }

      try {
        const response = await instance.handleRedirectPromise()
        if (response) {
          instance.setActiveAccount(response.account)
          user.email = response.account?.username ?? ''
          // Set default admin access for development
          user.admin = true
          return
        }

        if (isAuthenticated) {
          const accounts = instance.getAllAccounts()
          if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0])
            user.email = accounts[0]?.username ?? ''
            user.admin = true
          }
        } else {
          await instance.loginRedirect(loginRequest)
        }
      } catch (error) {
        console.error('Authentication error:', error)
        await instance.loginRedirect(loginRequest)
      }
    }

    handleAuth()
  }, [inProgress, isAuthenticated, instance])

  if (inProgress === 'login') {
    return <div>Signing in...</div>
  }

  return isAuthenticated ? <App /> : <div>Please wait while we redirect you to sign in...</div>
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <AppContainer />
    </MsalProvider>
  </React.StrictMode>,
)

reportWebVitals()
